import React, { useEffect, useState } from 'react';
import '../../theme/semantic.css';
import './ThemeSwitcher.css';
import { useTheme } from '../../contexts/ThemeContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { UsageEventsTracker } from '../../services/UsageEventsTracker';

// Simple curated subset order for user selection; can expand dynamically
const ORDER = ['primaryTheme','docuHounds','scholarMode','darkLab','zenFocus','retroVibes','cafeMode','stemEngine','grindMode','midnightGold','velvetNight','steelCore','neonCircuit','crimsonFocus','softBloom','skylineBlue'];

export default function ThemeSwitcher({ compact = false }) {
  const { currentTheme, setTheme, themes } = useTheme();
  const { patchPreferences } = usePreferences();
  const [open, setOpen] = useState(false);
  const ordered = ORDER.filter(k => themes[k]).map(k => ({ key: k, name: themes[k].name }));

  // Close on outside click (basic)
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const el = document.querySelector('.theme-switcher');
      if (el && !el.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="theme-switcher" aria-label="Theme">
      <button type="button" className="theme-switcher__btn" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open}>
        <span aria-hidden="true">🎨</span>
        {!compact && <span>{themes[currentTheme]?.name || 'Theme'}</span>}
      </button>
      {open && (
        <ul className="theme-switcher__list" role="listbox" aria-activedescendant={`theme-${currentTheme}`} tabIndex={0}>
          {ordered.map(o => (
            <li key={o.key} id={`theme-${o.key}`}> 
              <button
                type="button"
                className="theme-switcher__option"
                role="option"
                aria-selected={currentTheme === o.key}
                onClick={async () => {
                  const prev = currentTheme;
                  const newTheme = o.key;
                  setTheme(newTheme);
                  setOpen(false);
                  // Usage event (unchanged logic)
                  UsageEventsTracker.recordQueued({
                    eventType:'THEME_CHANGE',
                    payload:{ action:'change', category:'theme', entityType:'theme', entityId:newTheme, label:'Theme Switcher', metadata:{ previous: prev, next: newTheme } }
                  });
                  UsageEventsTracker.flush();
                  // Persist via shared PreferencesContext (handles debounce + fallback)
                  try {
                    await patchPreferences({ theme: newTheme });
                  } catch (e) {
                    // eslint-disable-next-line no-console
                    console.warn('[ThemeSwitcher] Failed to persist theme', e);
                  }
                }}
              >
                <span>{o.name}</span>
                {currentTheme === o.key && <span aria-hidden="true">✓</span>}
              </button>
            </li>
          ))}
        </ul>
      )}

    </div>
  );
}
